import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TablePagination,
  Button,
  IconButton,
  Input,
  Modal,
  TextField,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Edit } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { toast } from "react-toastify";
import FileSaver from "file-saver";
import { bulkUploadConfigService } from "../../utils/services/bulk-upload-config.service";
import { formatKey } from "../../utils/utility";
// import ContinuousProgressLoader from "../continuous-progress-loader/ContinuousProgressLoader";

const ImportResult = ({
  bulkUploadConfig,
  onFinish,
  type,
  onProceedClick = null,
}) => {
  const [uploadConfigData, setUploadConfigData] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkUploadResults, setBulkUploadResults] = useState([]);
  const [bulkUploadStatus, setBulkUploadStatus] = useState(true);
  const [open, setOpen] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [statusFilter, setStatusFilter] = useState(null); // null = all, 'success', 'failed'
  const [totalRecords, setTotalRecords] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  const intervalRef = useRef(null);

  useEffect(() => {
    if (bulkUploadConfig && bulkUploadConfig._id) {
      fetchUploadConfig();
      fetchStatus();
      startAutoRefresh();
    }
    return () => {
      stopAutoRefresh();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkUploadConfig?._id]);

  // Fetch results when page or rowsPerPage changes
  useEffect(() => {
    if (
      bulkUploadConfig &&
      bulkUploadConfig._id &&
      bulkUploadStatus?.total_records >= 0
    ) {
      fetchResults(bulkUploadStatus, page, rowsPerPage, statusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Fetch results when status filter changes
  useEffect(() => {
    if (
      bulkUploadConfig &&
      bulkUploadConfig._id &&
      bulkUploadStatus?.total_records >= 0
    ) {
      setPage(0);
      fetchResults(bulkUploadStatus, 0, rowsPerPage, statusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const startAutoRefresh = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      fetchStatus();
    }, 3000);
  };

  const stopAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const fetchStatus = async () => {
    let response = await bulkUploadConfigService.fetchUploadStatus(
      bulkUploadConfig.upload_status,
    );
    fetchResults(response);
    setBulkUploadStatus(response);
    if (response?.processed >= response?.total_records) {
      fetchResultCounts();
    }
  };

  // bulkUploadStatus.success/error are never populated by the backend, so
  // derive the actual counts from the bulk-upload-results collection instead.
  const fetchResultCounts = async () => {
    try {
      const [validRes, invalidRes] = await Promise.all([
        bulkUploadConfigService.fetchUploadResults(
          bulkUploadConfig.upload_status,
          0,
          0,
          "Valid",
        ),
        bulkUploadConfigService.fetchUploadResults(
          bulkUploadConfig.upload_status,
          0,
          0,
          "Invalid",
        ),
      ]);
      setSuccessCount(validRes?.total || 0);
      setFailedCount(invalidRes?.total || 0);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchResults = async (
    status,
    currentPage = page,
    currentRowsPerPage = rowsPerPage,
    filterStatus = statusFilter,
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;

    let response = await bulkUploadConfigService.fetchUploadResults(
      bulkUploadConfig.upload_status,
      currentRowsPerPage,
      currentPage * currentRowsPerPage,
      filterStatus,
      signal,
    );

    // Stop auto-refresh when processing is complete
    if (status?.processed >= status?.total_records) {
      console.log("Processing completed");
      if (intervalRef.current) stopAutoRefresh();
    } else {
      if (!intervalRef.current) {
        startAutoRefresh();
      }
    }

    setBulkUploadResults(response.data);
    setTotalRecords(response.total);
    return response.data;
  };

  const fetchUploadConfig = async () => {
    try {
      const response = await bulkUploadConfigService.findConfigById(
        bulkUploadConfig._id,
      );
      setUploadConfigData(response);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Fetch results for the new page
    fetchResults(bulkUploadStatus, newPage, rowsPerPage, statusFilter);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Fetch results for the new page size, starting from page 0
    fetchResults(bulkUploadStatus, 0, newRowsPerPage, statusFilter);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1100,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    overflow: "scroll",
    maxHeight: "80vh",
  };

  const onClose = () => {
    setOpen(false);
    setSelectedOrder({});
  };

  const updateRecord = async () => {
    let response = await bulkUploadConfigService.updateUploadResult({
      _id: selectedOrder._id,
      item: selectedOrder.item,
      mapped_column: uploadConfigData.mapped_column,
      type: type,
    });
    console.log(response);
    setBulkUploadResults(
      bulkUploadResults.map((x) => (x._id == response._id ? response : x)),
    );
    setTimeout(() => {
      fetchStatus();
    }, 1000);
    onClose();
  };

  const getCombinedValue = (item, columnStr) =>
    String(columnStr || "")
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean)
      .reduce((obj, col) => {
        obj[col] = item?.[col];
        return obj;
      }, {});

  console.log("bulkUploadResults", bulkUploadResults);

  const onProceed = async () => {
    if (onProceedClick) {
      let totalRecords = await fetchResults(
        null,
        0,
        bulkUploadStatus?.total_records,
        "Valid",
      );
      onProceedClick(totalRecords.map((x) => x.item));
    } else {
      let response = await bulkUploadConfigService.startImport({
        mapped_column: uploadConfigData.mapped_column,
        upload_status: uploadConfigData.upload_status,
        client_id: uploadConfigData.client_id,
        type: uploadConfigData.type,
      });

      toast.success(
        `Import started in the background. ${response.count}/${bulkUploadStatus.processed} records will be uploaded.`,
        { autoClose: 3000 },
      );
    }
    onFinish();
  };

  const handleDownloadResults = async (status) => {
    try {
      const response = await bulkUploadConfigService.downloadUploadResults(
        bulkUploadConfig.upload_status,
        status,
      );
      if (!response?.base64) throw new Error("Invalid file response");

      const sliceSize = 1024;
      const byteCharacters = atob(response.base64);
      const byteArrays = [];
      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }

      FileSaver.saveAs(
        new Blob(byteArrays, { type: "application/vnd.ms-excel" }),
        response.fileName ||
          `${status === "Valid" ? "success" : "failed"}-records.xlsx`,
      );
    } catch (error) {
      console.error("Error downloading the results:", error);
      toast.error("Failed to download records.");
    }
  };

  // Check if processing is complete
  const isProcessingComplete =
    bulkUploadStatus?.processed >= bulkUploadStatus?.total_records;

  // Check if there are any errors (bulkUploadStatus.error is never populated
  // by the backend, so this is derived from the actual results count)
  const hasErrors = failedCount > 0;

  // Calculate progress percentage
  const progressPercentage =
    bulkUploadStatus?.total_records > 0
      ? Math.round(
          (bulkUploadStatus.processed / bulkUploadStatus.total_records) * 100,
        )
      : 0;

  return (
    <>
      <Box sx={{ width: "100%" }}>
        {/* Toolbar: controls row + status row */}
        {bulkUploadStatus && (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "#FFF8F3",
              borderRadius: 2,
              border: "1px solid #FFE5D3",
              mb: 1.5,
            }}
          >
            {/* Controls row: filters (left) + actions (right) */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--text-tertiary)",
                  }}
                >
                  Filter:
                </Typography>
                <Button
                  size="small"
                  variant={statusFilter === null ? "contained" : "outlined"}
                  onClick={() => setStatusFilter(null)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.5,
                    fontFamily: "Poppins",
                    fontSize: "12px",
                  }}
                >
                  All
                </Button>
                <Button
                  size="small"
                  variant={statusFilter === "Valid" ? "contained" : "outlined"}
                  onClick={() => setStatusFilter("Valid")}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.5,
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    color: statusFilter === "Valid" ? "#fff" : "#27ab86",
                    borderColor: "#27ab86",
                  }}
                >
                  Success
                </Button>
                <Button
                  size="small"
                  variant={
                    statusFilter === "Invalid" ? "contained" : "outlined"
                  }
                  onClick={() => setStatusFilter("Invalid")}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.5,
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    color: statusFilter === "Invalid" ? "#fff" : "#a0452f",
                    borderColor: "#a0452f",
                  }}
                >
                  Failed
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {isProcessingComplete && successCount > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDownloadResults("Valid")}
                    startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 1.75,
                      py: 0.5,
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      color: "#27ab86",
                      borderColor: "#27ab86",
                    }}
                  >
                    Success Records
                  </Button>
                )}
                {isProcessingComplete && hasErrors && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDownloadResults("Invalid")}
                    startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 1.75,
                      py: 0.5,
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      color: "#a0452f",
                      borderColor: "#a0452f",
                    }}
                  >
                    Failed Records
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() => {
                    if (isProcessingComplete) {
                      onProceed();
                    }
                  }}
                  disabled={!isProcessingComplete}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {!isProcessingComplete && (
                      <AccessTimeIcon sx={{ fontSize: 15, color: "#FF8C42" }} />
                    )}
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: !isProcessingComplete ? "#FF8C42" : "#FFF",
                      }}
                    >
                      {isProcessingComplete
                        ? "Proceed and Import"
                        : "Processing..."}
                    </Typography>
                  </Box>
                </Button>

                <Button
                  size="small"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#FF4545",
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: "#E63939",
                    },
                  }}
                  variant="contained"
                  onClick={() => fetchStatus()}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <CachedIcon sx={{ fontSize: 15, color: "#fff" }} />
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#fff",
                      }}
                    >
                      Refresh
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </Box>

            {/* Status row: file being processed + progress summary */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mt: 1.25,
                pt: 1.25,
                borderTop: "1px solid #FFE5D3",
              }}
            >
              {bulkUploadStatus.file_name ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: 16, color: "#FF8C42", flexShrink: 0 }}
                  />
                  <Typography
                    noWrap
                    title={bulkUploadStatus.file_name}
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    {bulkUploadStatus.file_name}
                  </Typography>
                </Box>
              ) : (
                <span />
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor:
                      isProcessingComplete && !hasErrors
                        ? "#27ab86"
                        : "#FF8C42",
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#333",
                    whiteSpace: "nowrap",
                  }}
                >
                  {bulkUploadStatus.processed}/{bulkUploadStatus.total_records}{" "}
                  records &middot; {progressPercentage}% complete
                </Typography>
              </Box>
            </Box>

            {/* Progress bar */}
            <Box
              sx={{
                width: "100%",
                height: 6,
                backgroundColor: "#FFE5D3",
                borderRadius: "4px",
                overflow: "hidden",
                mt: 1,
              }}
            >
              <Box
                sx={{
                  width: `${progressPercentage}%`,
                  height: "100%",
                  backgroundColor:
                    isProcessingComplete && !hasErrors
                      ? "#27ab86"
                      : "#FF8C42",
                  transition: "width 0.3s ease-in-out",
                }}
              />
            </Box>
          </Box>
        )}
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 270px)",
            overflowY: "auto",
            overflowX: "auto",
            height: "100%",
            mt: 1,
          }}
          component={Paper}
        >
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {Array.isArray(uploadConfigData.mapped_column) &&
                  uploadConfigData.mapped_column
                    .slice()
                    .sort((a, b) => (a.order || 999) - (b.order || 999))
                    .map((x) => (
                      <TableCell
                        key={x.key}
                        sx={{ fontWeight: "600", fontSize: 12 }}
                      >
                        {formatKey(x.key)}
                      </TableCell>
                    ))}
                <TableCell sx={{ fontWeight: "600", fontSize: 12 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: 12 }}>
                  Message
                </TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: 12 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(bulkUploadResults || []).map((uploadItem) => (
                <TableRow key={uploadItem._id}>
                  {Array.isArray(uploadConfigData?.mapped_column) &&
                    uploadConfigData.mapped_column.map((x) => {
                      const value =
                        x.operator === "combine"
                          ? getCombinedValue(uploadItem.item, x.column)
                          : uploadItem.item?.[
                              x.column === "user_constant" ? x.type : x.column
                            ];
                      return (
                        <TableCell key={`${uploadItem._id}-${x.key}`}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: uploadItem.error ? "#a0452f" : "#27ab86",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              fontFamily: "Poppins",
                              width: 200,
                              maxHeight: 50,
                            }}
                          >
                            {value !== undefined && value !== null
                              ? typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)
                              : ""}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  <TableCell>
                    {uploadItem.error ? (
                      <Typography sx={{ color: "#a0452f", fontSize: 12 }}>
                        Failed
                      </Typography>
                    ) : (
                      <Typography sx={{ color: "#27ab86", fontSize: 12 }}>
                        Success
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        color: uploadItem.error ? "#a0452f" : "#27ab86",
                      }}
                    >
                      {uploadItem.message}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", fontSize: 12 }}>
                    <IconButton
                      onClick={() => {
                        setSelectedOrder(uploadItem);
                        setOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "var(--bg-primary)",
              padding: "2px",
              borderTop: "1px solid var(--border-color)",
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100, totalRecords || 150]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showLastButton
            showFirstButton
          />
        </TableContainer>
      </Box>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            align="center"
            variant="h5"
            component="h2"
          >
            {`Edit ${type} details`}
          </Typography>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              marginTop: 10,
            }}
          >
            {bulkUploadConfig.file_column.map(
              (x, index) =>
                index % 2 === 0 && (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginRight: 15,
                      }}
                    >
                      <Typography
                        style={{
                          width: "200px",
                          textAlign: "left",
                          color: (selectedOrder.columns || []).includes(x)
                            ? "red"
                            : "var(--text-primary)",
                        }}
                      >
                        {x}
                      </Typography>
                      <TextField
                        variant="standard"
                        fullWidth
                        value={selectedOrder?.item?.[x] || ""}
                        onChange={(e) =>
                          setSelectedOrder({
                            ...selectedOrder,
                            item: {
                              ...selectedOrder.item,
                              [x]: e.target.value,
                            },
                          })
                        }
                        InputProps={{
                          style: {
                            color: (selectedOrder.columns || []).includes(x)
                              ? "red"
                              : "var(--text-primary)",
                            borderBottom: (
                              selectedOrder.columns || []
                            ).includes(x)
                              ? "1px solid var(--border-color)"
                              : undefined, // MUI uses underline by default in standard variant
                          },
                        }}
                      />
                    </div>

                    {bulkUploadConfig.file_column[index + 1] && (
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <Typography
                          style={{
                            width: "200px",
                            textAlign: "left",
                            color: (selectedOrder.columns || []).includes(
                              bulkUploadConfig.file_column[index + 1],
                            )
                              ? "red"
                              : "var(--text-primary)",
                          }}
                        >
                          {bulkUploadConfig.file_column[index + 1]}
                        </Typography>
                        <Input
                          style={{
                            flex: "1",
                            border: (selectedOrder.columns || []).includes(
                              bulkUploadConfig.file_column[index + 1],
                            )
                              ? "1px solid red"
                              : "0px solid var(--text-primary)",
                            color: (selectedOrder.columns || []).includes(
                              bulkUploadConfig.file_column[index + 1],
                            )
                              ? "red"
                              : "var(--text-primary)",
                          }}
                          value={
                            selectedOrder?.item?.[
                              bulkUploadConfig.file_column[index + 1]
                            ]
                          }
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              item: {
                                ...selectedOrder.item,
                                [bulkUploadConfig.file_column[index + 1]]:
                                  e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                ),
            )}
          </div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 2,
            }}
          >
            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                marginRight: 3,
              }}
              onClick={onClose}
            >
              <Typography
                fontFamily={"poppins"}
                fontSize={"14px"}
                fontWeight={500}
                color="primary"
              >
                Cancel
              </Typography>
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                textTransform: "none",
              }}
              onClick={() => updateRecord()}
            >
              <Typography
                fontFamily={"poppins"}
                fontSize={"14px"}
                fontWeight={500}
                color="#fff"
              >
                Update
              </Typography>
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ImportResult;
